import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Typography } from '@biome-design-system/ui/Typography';
import { Box } from '@biome-design-system/ui/Box';
import type { PMCConfig } from '@kyruus/types';
import { useIntl } from '@kyruus/intl';
import type { GetUpdatedSearch, SearchSummary, Log } from 'Src/types';
import type { SearchLink } from 'Src/types/search';
import { getConfigProperty } from 'Common/config';
import { SuggestedSearches } from './SuggestedSearches';
import { CommonSearches } from './CommonSearches';
import { getBaseQueryMods } from './utils';
import { messages } from './messages';

interface NoResultsContentProps {
  config: PMCConfig;
  getUpdatedSearch: GetUpdatedSearch;
  log: Log;
  searchSummary: SearchSummary;
  suggestions: SearchLink[];
}
export const NoResultsContent = ({
  config,
  getUpdatedSearch,
  log,
  searchSummary,
  suggestions
}: NoResultsContentProps) => {
  const intl = useIntl();
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const baseQueryMods = getBaseQueryMods({ queryParams });
  const searchTerm = searchSummary?.search_description;
  const showSearchSuggestions =
    getConfigProperty(config, 'KENG_38276_show_search_suggestions_v9', true) &&
    suggestions?.length;
  const hideCommonSearches = getConfigProperty(
    config,
    'no_results_page.hide_common_searches',
    false
  );

  return (
    <div>
      <Box
        sx={(theme) => ({
          marginTop: theme.spacing(4),
          marginBottom: theme.spacing(4)
        })}
      >
        <Typography
          variant="h2"
          sx={(theme) => ({
            marginBottom: theme.spacing(2)
          })}
        >
          {searchTerm
            ? intl.formatMessage(messages.noResultsFor, { searchTerm })
            : intl.formatMessage(messages.noResultsNoSearchTerm)}
        </Typography>
        <Typography variant="body1">
          {intl.formatMessage(messages.noResultsSubtitle)}
        </Typography>
      </Box>
      {showSearchSuggestions ? (
        <SuggestedSearches
          suggestions={suggestions}
          baseQueryMods={baseQueryMods}
          getUpdatedSearch={getUpdatedSearch}
          log={log}
        />
      ) : null}
      {hideCommonSearches ? null : (
        <CommonSearches
          getUpdatedSearch={getUpdatedSearch}
          log={log}
          baseQueryMods={baseQueryMods}
          config={config}
        />
      )}
    </div>
  );
};
